/**
 * Test play (W2-02; DESIGN.md §4.9, ARCHITECTURE.md §7.5, rendering.md R§6.2): Phira's game state machine inside the
 * editor, autoplay or judged.
 *
 *   intro    0.7 s (game time = real time x rate): res.alpha = 1 - (1 - g/0.7)^3, the HUD slides in with the same
 *            curve, chart time pinned at the start; then the music starts at the start time;
 *   playing  chart time from the transport; judged play feeds input to the JudgeSession (judge.ts);
 *   outro    after the track end + 0.5 s (or when the music stops at its end): res.alpha = 1 - min(e/0.7, 1)^2,
 *            HUD p = 1 - min(e/1, 1)^2, chart time frozen; after 1 s the results (judged) or the finish panel;
 *   paused   Esc: transport stopped, pause menu (Resume, Restart, Back to editor here, Back to where I started,
 *            mode, rate).
 * Test play starts 2 beats (setting) before the playhead (F5 / Ctrl+Enter) or at the chart start (Shift+F5), ignores
 * the loop region and the preview-only filters, and in judged play the audio scheduler gets no emission source:
 * hits play their sound through `audio.playHitsound`. The chart offset is never changed.
 * Edge cases: a rate change applies to the intro / outro clocks and the transport at once (judgement windows are in
 * chart time); a seek back during judged play (a command run with a Ctrl / Alt chord) restarts the judgement there,
 * so the notes played again are judged afresh instead of staying "already judged"; leaving always reveals the
 * restored time in the lane.
 *
 * One controller per AppServices (`testPlayOf`); React reads it through subscribe/getSnapshot.
 */

import type { AppServices } from '../../app/registry/types';
import type { Engine, JudgeView, PreviewFilters } from '../../core/engine/types';
import type { ChartDoc, NoteId } from '../../core/model/types';
import type { Sec } from '../../core/time/types';
import type { Unsubscribe } from '../../core/util/types';
import { NO_FILTERS, previewFiltersOf } from './filters';
import {
  defaultSound,
  FlickDetector,
  JudgeSession,
  type HitSound,
  type JudgeEvent,
  type JudgeResults,
  type JudgeSource,
} from './judge';
import { appKey, liveServices } from './live';
import { EXT_LEAD_IN, EXT_TEST_MODE, leadInBeatsOf, readExt, testPlayModeOf, type TestPlayMode } from './prefs';
import { PREVIEW_FRAME_ID } from '../../render/types';

export const INTRO_SEC = 0.7;
export const OUTRO_FADE_SEC = 0.7;
export const OUTRO_HUD_SEC = 1;
/** Results appear this long after the outro starts. */
export const RESULTS_DELAY_SEC = 1;
/** Playing ends this long after the track end. */
export const END_PAD_SEC = 0.5;
/** A playing frame this much earlier than the previous one is a seek back (time never runs backwards otherwise). */
export const SEEK_BACK_SEC = 0.05;

export type TestPlayPhase = 'idle' | 'intro' | 'playing' | 'paused' | 'outro' | 'finished';

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Intro fade (and HUD slide) at game time g since the start. */
export function introProgress(g: number): number {
  return 1 - (1 - clamp01(g / INTRO_SEC)) ** 3;
}

/** Outro fade at game time e since the outro started. */
export function outroAlpha(e: number): number {
  return 1 - Math.min(Math.max(e, 0) / OUTRO_FADE_SEC, 1) ** 2;
}

/** Outro HUD slide progress p at game time e since the outro started. */
export function outroHud(e: number): number {
  return 1 - Math.min(Math.max(e, 0) / OUTRO_HUD_SEC, 1) ** 2;
}

/**
 * The one reveal of every "back to where I started" path (UX-055: test play's exit, Theater's Esc): scrolls the lane
 * and motion columns to `sec` so the editor never shows the part of the chart the play had followed to.
 */
export function revealAtSec(services: Pick<AppServices, 'engine' | 'timeline'>, sec: Sec): void {
  const tempo = services.engine?.tempo;
  if (tempo === undefined) return;
  try {
    services.timeline?.reveal(tempo.beatF64At(sec), 'playhead');
  } catch {
    // The timeline may be mid-mount; the playhead still moved.
  }
}

export interface TestPlaySnapshot {
  readonly active: boolean;
  readonly phase: TestPlayPhase;
  readonly mode: TestPlayMode;
  readonly from: 'playhead' | 'start';
  /** Chart time play starts from (lead-in before the playhead, or 0). */
  readonly startSec: Sec;
  /** The playhead when test play was launched ("Back to where I started"). */
  readonly originSec: Sec;
  /** Phase interrupted by the pause menu (null when not paused). */
  readonly pausedFrom: TestPlayPhase | null;
  /** Results of the judged session (finished, or on demand when leaving). */
  readonly results: JudgeResults | null;
  /** Bumps on every input judgement (accessible live region of the play screen). */
  readonly lastEvent: JudgeEvent | null;
}

/** What the preview stage draws for a frame of test play. */
export interface TestPlayFrame {
  readonly t: Sec;
  readonly resAlpha: number;
  readonly hudProgress: number;
  readonly judge: JudgeView | null;
  readonly filters: PreviewFilters;
  /** Request the next frame even when the transport is paused (intro / outro animate). */
  readonly animating: boolean;
}

const IDLE: TestPlaySnapshot = Object.freeze({
  active: false,
  phase: 'idle',
  mode: 'autoplay',
  from: 'playhead',
  startSec: 0,
  originSec: 0,
  pausedFrom: null,
  results: null,
  lastEvent: null,
});

/** Builds the judge's note source over the engine and the document. */
export function engineJudgeSource(engine: Engine, doc: ChartDoc): JudgeSource {
  return {
    notesInWindow: (t0, t1) => engine.notesInWindow(Number.isFinite(t0) ? t0 : -1e9, t1),
    note: (id: NoteId) => {
      const n = doc.notes.get(id);
      if (n === undefined || n.fake) return null;
      const time = engine.noteSec(id);
      const end = n.kind === 'hold' ? engine.noteEndSec(id) : time;
      let sound: HitSound = defaultSound(n.kind);
      if (n.hitsound !== null) {
        sound =
          n.hitsound.kind === 'asset'
            ? { kind: 'asset', asset: n.hitsound.asset }
            : { kind: 'skin', name: n.hitsound.sound === 'tap' ? 'click' : n.hitsound.sound };
      }
      return { id, kind: n.kind, line: n.line, time, end, sound };
    },
  };
}

/** Length of the played track: the music, else the chart's own end (engine max time). */
export function trackLengthOf(services: Pick<AppServices, 'audio' | 'engine'>): Sec {
  const d = services.audio.music?.durationSec;
  if (d !== undefined && d > 0) return d;
  return services.engine?.maxTime ?? 0;
}

function nowMs(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

export class TestPlayController {
  private readonly services: AppServices;
  private snap: TestPlaySnapshot = IDLE;
  private readonly listeners = new Set<() => void>();
  private judge: JudgeSession | null = null;
  private introStart: number | null = null;
  private outroStart: number | null = null;
  private outroT = 0;
  private pausedAt = 0;
  private playStarted = false;
  /** Chart time of the last 'playing' frame (a later frame earlier than this means a seek back). */
  private lastPlayT: number | null = null;
  private savedLoop: { readonly start: Sec; readonly end: Sec } | null = null;
  readonly flicks = new FlickDetector();

  constructor(services: AppServices) {
    this.services = services;
  }

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  getSnapshot = (): TestPlaySnapshot => this.snap;

  private set(patch: Partial<TestPlaySnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const cb of this.listeners) cb();
    this.services.scheduler.request(PREVIEW_FRAME_ID);
  }

  get active(): boolean {
    return this.snap.active;
  }

  /** The judged session (null in autoplay). */
  get judgeView(): JudgeView | null {
    return this.judge;
  }

  private mode(): TestPlayMode {
    return readExt(this.services.settings, EXT_TEST_MODE, testPlayModeOf);
  }

  private newJudge(start: Sec): JudgeSession | null {
    const { engine, store } = this.services;
    if (engine === null || store === null) return null;
    const j = new JudgeSession(engineJudgeSource(engine, store.doc));
    j.start(start, trackLengthOf(this.services));
    return j;
  }

  /** Start test play (from 2 beats before the playhead, or from the chart start). */
  async start(from: 'playhead' | 'start'): Promise<void> {
    const { engine, audio } = this.services;
    if (engine === null || this.services.store === null) return;
    const transport = audio.transport;
    const wasActive = this.snap.active;
    const origin = wasActive ? this.snap.originSec : transport.chartTime();
    if (transport.state.playing) transport.stop({ returnToStart: false });
    let startSec = 0;
    if (from === 'playhead') {
      const lead = readExt(this.services.settings, EXT_LEAD_IN, leadInBeatsOf);
      const beat = engine.tempo.beatF64At(origin);
      startSec = Math.max(0, engine.tempo.secAtBeatF64(Math.max(0, beat - lead)));
    }
    if (!wasActive) this.savedLoop = transport.state.loop;
    transport.setLoop(null);
    transport.seek(startSec);
    const mode = this.mode();
    this.judge = mode === 'judged' ? this.newJudge(startSec) : null;
    this.introStart = null;
    this.outroStart = null;
    this.playStarted = false;
    this.lastPlayT = null;
    this.flicks.reset();
    this.set({
      active: true,
      phase: 'intro',
      mode,
      from,
      startSec,
      originSec: origin,
      pausedFrom: null,
      results: null,
      lastEvent: null,
    });
    syncEmissionSource(this.services);
    try {
      await audio.ensureContext();
    } catch {
      // No audio device: the play still runs (silent); the transport clock keeps time.
    }
  }

  /** Restart from the same start time. */
  restart(): void {
    if (!this.snap.active) return;
    void this.start(this.snap.from);
  }

  pause(): void {
    const phase = this.snap.phase;
    if (phase !== 'intro' && phase !== 'playing') return;
    const transport = this.services.audio.transport;
    this.pausedAt = phase === 'playing' ? transport.chartTime() : this.snap.startSec;
    this.lastPlayT = null;
    if (transport.state.playing) transport.stop({ returnToStart: false });
    this.flicks.reset();
    this.set({ phase: 'paused', pausedFrom: phase });
  }

  resume(): void {
    if (this.snap.phase !== 'paused') return;
    if (this.snap.pausedFrom === 'intro') {
      this.introStart = null;
      this.set({ phase: 'intro', pausedFrom: null });
      return;
    }
    void this.services.audio.transport.play({ from: this.pausedAt });
    this.set({ phase: 'playing', pausedFrom: null });
  }

  /** Switch autoplay / judged (remembered); a judged session starts at the current time. */
  setMode(mode: TestPlayMode): void {
    this.services.settings.setExt(EXT_TEST_MODE, mode);
    if (!this.snap.active || mode === this.snap.mode) {
      if (this.snap.active) this.set({ mode });
      return;
    }
    const t = this.snap.phase === 'paused' ? this.pausedAt : this.currentTime();
    this.judge = mode === 'judged' ? this.newJudge(Math.max(this.snap.startSec, t)) : null;
    this.lastPlayT = null;
    this.set({ mode, results: null });
    syncEmissionSource(this.services);
  }

  private currentTime(): Sec {
    switch (this.snap.phase) {
      case 'intro':
        return this.snap.startSec;
      case 'paused':
        return this.pausedAt;
      case 'outro':
      case 'finished':
        return this.outroT;
      default:
        return this.services.audio.transport.chartTime();
    }
  }

  /** Leave test play: 'here' keeps the playhead where the play was, 'start' restores where it was launched. */
  exit(where: 'here' | 'start'): void {
    if (!this.snap.active) return;
    const transport = this.services.audio.transport;
    const here = this.currentTime();
    if (transport.state.playing) transport.stop({ returnToStart: false });
    transport.setLoop(this.savedLoop);
    this.seekAndReveal(where === 'here' ? here : this.snap.originSec);
    this.judge = null;
    this.flicks.reset();
    this.set({ ...IDLE, mode: this.snap.mode });
    syncEmissionSource(this.services);
  }

  /**
   * The one return path of test play (UX-055): seeks the transport and scrolls the lane and motion columns to the
   * same time, so the editor never shows the part of the chart the play had followed to.
   */
  private seekAndReveal(sec: Sec): void {
    this.services.audio.transport.seek(sec);
    revealAtSec(this.services, sec);
  }

  /** Results of the judged session so far (null in autoplay). */
  currentResults(): JudgeResults | null {
    return this.judge?.results() ?? null;
  }

  // -------------------------------------------------------------------------------------------------------------
  // Input (judged play)
  // -------------------------------------------------------------------------------------------------------------

  private accepting(): boolean {
    return this.snap.active && this.snap.phase === 'playing' && this.judge !== null;
  }

  private emit(events: readonly JudgeEvent[]): void {
    if (events.length === 0) return;
    for (const e of events) if (e.sound !== null) this.services.audio.playHitsound(e.sound);
    const last = events[events.length - 1] ?? null;
    this.set({ lastEvent: last });
  }

  press(touch: string): void {
    if (!this.accepting() || this.judge === null) return;
    this.emit(this.judge.press(this.services.audio.transport.chartTime(), touch));
  }

  release(touch: string): void {
    if (this.judge === null || !this.snap.active) return;
    if (this.snap.phase !== 'playing') return;
    this.emit(this.judge.release(this.services.audio.transport.chartTime(), touch));
  }

  flick(): void {
    if (!this.accepting() || this.judge === null) return;
    this.emit(this.judge.flick(this.services.audio.transport.chartTime()));
  }

  /** Mouse movement (CSS px) for flick detection. */
  pointerMove(x: number, y: number, ms: number): void {
    if (!this.accepting() || this.judge === null) return;
    if (this.flicks.move(x, y, ms, this.judge.touching)) this.flick();
  }

  // -------------------------------------------------------------------------------------------------------------
  // Frames
  // -------------------------------------------------------------------------------------------------------------

  /** Advances the state machine to wall time `now` (ms) and returns what to draw. */
  frame(now: number = nowMs()): TestPlayFrame {
    const transport = this.services.audio.transport;
    const rate = transport.state.rate > 0 ? transport.state.rate : 1;
    const base = { judge: this.judge, filters: NO_FILTERS };
    switch (this.snap.phase) {
      case 'intro': {
        this.introStart ??= now;
        const g = ((now - this.introStart) / 1000) * rate;
        const p = introProgress(g);
        if (g >= INTRO_SEC) {
          this.playStarted = true;
          void transport.play({ from: this.snap.startSec });
          this.set({ phase: 'playing' });
        }
        return { ...base, t: this.snap.startSec, resAlpha: p, hudProgress: p, animating: true };
      }
      case 'playing': {
        const t = transport.chartTime();
        if (this.judge !== null && this.lastPlayT !== null && t < this.lastPlayT - SEEK_BACK_SEC) {
          // Seeked back: judge from here again (the notes ahead were already judged in the session).
          this.judge = this.newJudge(Math.max(this.snap.startSec, t));
          this.set({ lastEvent: null });
        }
        this.lastPlayT = t;
        if (this.judge !== null) this.emit(this.judge.update(t));
        const end = trackLengthOf(this.services);
        const ended = t >= end + END_PAD_SEC || (this.playStarted && !transport.state.playing && t >= end - 0.05);
        if (ended) {
          this.outroStart = now;
          this.outroT = Math.min(t, end + END_PAD_SEC);
          if (transport.state.playing) transport.stop({ returnToStart: false });
          this.set({ phase: 'outro' });
        }
        return { ...base, t, resAlpha: 1, hudProgress: 1, animating: false };
      }
      case 'outro': {
        this.outroStart ??= now;
        const e = ((now - this.outroStart) / 1000) * rate;
        if (e >= RESULTS_DELAY_SEC) {
          this.set({ phase: 'finished', results: this.judge?.results() ?? null });
        }
        return { ...base, t: this.outroT, resAlpha: outroAlpha(e), hudProgress: outroHud(e), animating: true };
      }
      case 'paused':
        return { ...base, t: this.pausedAt, resAlpha: 1, hudProgress: 1, animating: false };
      case 'finished':
        return { ...base, t: this.outroT, resAlpha: 0, hudProgress: 0, animating: false };
      default:
        return { ...base, t: transport.chartTime(), resAlpha: 1, hudProgress: 1, animating: false };
    }
  }

  dispose(): void {
    if (this.snap.active) this.exit('here');
    this.listeners.clear();
  }
}

const controllers = new WeakMap<object, TestPlayController>();

/** The test play controller of an app (created on first use, over the live services). */
export function testPlayOf(services: AppServices): TestPlayController {
  const key = appKey(services);
  let c = controllers.get(key);
  if (c === undefined) {
    c = new TestPlayController(liveServices(services));
    controllers.set(key, c);
  }
  return c;
}

/** The controller when one exists (no creation). */
export function peekTestPlay(services: AppServices): TestPlayController | null {
  return controllers.get(appKey(services)) ?? null;
}

/**
 * Installs the hitsound emission source matching the current state: none during judged test play (hits play their
 * sounds), the engine without preview filters during autoplay test play, else the engine with the editor's filters
 * (solo, hidden and "focus hitsounds" decide which lines sound). The filters are read on every request, so filter
 * changes need no re-install; engine swaps and test play changes call this again.
 */
export function syncEmissionSource(app: AppServices): void {
  const services = liveServices(app);
  const engine = services.engine;
  if (engine === null) {
    services.audio.setEmissionSource(null);
    return;
  }
  const tp = peekTestPlay(services)?.getSnapshot() ?? IDLE;
  if (tp.active && tp.mode === 'judged') {
    services.audio.setEmissionSource(null);
    return;
  }
  const filters = (): PreviewFilters => {
    if (tp.active) return NO_FILTERS;
    const st = services.editor.getState();
    return previewFiltersOf(st.filters, st.activeLine);
  };
  services.audio.setEmissionSource((t0, t1, rate) => engine.emissions(t0, t1, rate, filters()));
}
